import {gql} from '@apollo/client'
import {Link} from 'react-router-dom'
import React, {useState} from 'react'
import {ListLayout} from '.'
import {Button, LinkButton} from '../../../components/library/buttons'
import {List, ListColumns} from '../../../components/library/list'
import {OrganisationNode} from '../../../organisation'
import {
  GroupRowFragment,
  useGroupsQuery,
  useGetCurrentUserGroupsQuery,
  useDeleteWorkbenchGroupsMutation
} from './generated/groups'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {Helmet} from 'react-helmet-async'
import {
  DropdownBody,
  DropdownFooter,
  DropdownItem
} from '../../../components/library/dropdown/content'
import {OverflowFooter} from '../../../components/overflow'
import {Modal} from '../../../components/library/modal/modal'

const columns: ListColumns<
  GroupRowFragment & {isMember: boolean; fromSsoMapping: boolean}
> = [
  {
    id: 'group',
    header: 'Group',
    accessor: r => (
      <Link to={`../group/${r.id}`}>
        <strong>{r.name}</strong>
      </Link>
    )
  },
  {
    id: 'description',
    header: 'Description',
    accessor: r => r.description
  },
  {
    id: 'members',
    header: 'Number of members',
    accessor: r => r.businessUserGroups.totalCount
  },
  {
    id: 'ID',
    header: 'id',
    accessor: r => r.id
  },
  {
    id: 'membership',
    header: 'Are you a member?',
    accessor: r =>
      r.isMember ? (r.fromSsoMapping ? 'Yes, via SSO mapping' : 'Yes') : 'No'
  }
]

export const Groups = ({organisation}: {organisation: OrganisationNode}) => {
  const {data: currentGroupIdsData} = useGetCurrentUserGroupsQuery({
    variables: {organisationId: organisation.id}
  })
  const {data, loading, refetch} = useGroupsQuery({
    variables: {organisationId: organisation.id}
  })
  const canEdit = organisation.hasPermission(
    PermissionResource.Group,
    PermissionAction.Edit
  )

  return (
    <>
      <Helmet>
        <title>Workbench groups</title>
      </Helmet>
      <ListLayout
        header={
          <>
            <p>
              A group is a collection of roles, based on particular job
              functions. Workbench team members can belong to one or more
              groups. Groups have access to specific roles, and can be scoped to
              all or specific environments.
            </p>
            {canEdit ? (
              <LinkButton disabled={!canEdit} to="../group">
                Create a new group
              </LinkButton>
            ) : (
              // hack until link buttons support disabled styles
              <Button disabled={true}>Create a new group</Button>
            )}
          </>
        }
      >
        <List
          bleed={'#f2f2f2'}
          loading={loading && !data}
          columns={columns}
          rows={
            data?.groups?.nodes.map(n => {
              const matchedCurrentGroup =
                currentGroupIdsData?.currentBusinessUserGroupIds?.nodes.find(
                  c => c.groupId === n.id
                )
              if (matchedCurrentGroup) {
                return {
                  ...n,
                  isMember: true,
                  fromSsoMapping: matchedCurrentGroup.fromSsoMapping ?? false
                }
              }
              return {...n, isMember: false, fromSsoMapping: false}
            }) ?? []
          }
          renderOverflow={group => (
            <Overflow
              group={group}
              canEdit={canEdit}
              currentGroupIds={
                (currentGroupIdsData?.currentBusinessUserGroupIds?.nodes
                  .filter(n => !n.fromSsoMapping)
                  .map(n => n.groupId)
                  .filter(Boolean) as string[]) ?? []
              }
              organisationId={organisation.id}
              refetch={refetch}
            />
          )}
        />
      </ListLayout>
    </>
  )
}

const Overflow = ({
  group,
  canEdit,
  currentGroupIds,
  organisationId,
  refetch
}: {
  group: GroupRowFragment
  canEdit: boolean
  currentGroupIds: string[]
  organisationId: string
  refetch: () => void
}) => {
  const [deleteWorkbenchGroupsMutation] = useDeleteWorkbenchGroupsMutation()
  const [confirmationModal, setConfirmationModal] = useState(false)
  const userCount = group.businessUserGroups.totalCount
  const userInGroup = currentGroupIds.includes(group.id)

  return (
    <>
      {confirmationModal ? (
        <Modal
          title={
            userInGroup
              ? 'Cannot delete group'
              : `Confirm "${group.name}" deletion`
          }
          handleClose={() => setConfirmationModal(false)}
        >
          {userInGroup ? (
            <>
              <p>
                You cannot delete a group that you are currently a member of.
              </p>
              <div>
                <Button
                  appearance="secondary"
                  onClick={() => setConfirmationModal(false)}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <>
              <div>
                <p>
                  Are you sure you want to delete the group named "{group.name}
                  "?
                </p>
                <p>
                  {userCount
                    ? `${userCount} user${
                        userCount > 1 ? 's' : ''
                      } assigned to "${
                        group.name
                      }" will be effected. If they don't have any other groups assigned they will not be able to use the Atomic workbench.`
                    : ''}
                </p>
              </div>
              <div>
                <Button
                  style={{marginRight: '10px'}}
                  appearance="secondary"
                  onClick={() => setConfirmationModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={() => {
                    deleteWorkbenchGroupsMutation({
                      variables: {groupId: group.id, organisationId}
                    }).then(() => {
                      refetch()
                      setConfirmationModal(false)
                    })
                  }}
                >
                  Confirm
                </Button>
              </div>
            </>
          )}
        </Modal>
      ) : null}
      <DropdownBody>
        <DropdownItem disabled={!canEdit}>
          <Link to={`../group/${group.id}`}>Edit</Link>
        </DropdownItem>
        <DropdownItem disabled={!canEdit}>
          <a
            onClick={() => {
              setConfirmationModal(true)
            }}
          >
            Delete
          </a>
        </DropdownItem>
      </DropdownBody>
      <DropdownFooter>
        <OverflowFooter id={group.id}></OverflowFooter>
      </DropdownFooter>
    </>
  )
}

Groups.fragments = {
  row: gql`
    fragment GroupRow on Group {
      id
      name
      description
      businessUserGroups: businessUserGroupsByOrganisationIdAndGroupId {
        totalCount
      }
    }
  `
}

gql`
  query Groups($organisationId: String!) {
    groups(condition: {organisationId: $organisationId}, orderBy: [NAME_ASC]) {
      nodes {
        id
        ...GroupRow
      }
    }
  }
  ${Groups.fragments.row}

  query GetCurrentUserGroups($organisationId: String!) {
    currentBusinessUserGroupIds(organisationId: $organisationId) {
      nodes {
        groupId
        fromSsoMapping
      }
    }
  }

  mutation DeleteWorkbenchGroups($groupId: String!, $organisationId: String!) {
    deleteWorkbenchGroups(
      input: {organisationId: $organisationId, groupId: $groupId}
    ) {
      boolean
    }
  }
`
